import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { brand } from "../../theme/themePrimitives";
import { EllipsisTooltip } from "../EllipsisTooltip/EllipsisTooltip";

interface UserItem {
  id: string | number;
  title: string;
  subtitle?: string;
}

interface AssignmentItem {
  id: string | number;
  title: string;
}

interface CombinedSelectedItemsPreviewProps {
  users: UserItem[];
  assignments: AssignmentItem[];
}

export const CombinedSelectedItemsPreview: React.FC<
  CombinedSelectedItemsPreviewProps
> = ({ users, assignments }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  return (
    <>
      <Paper
        sx={{ mb: 2, p: "20px 24px 24px 24px", border: "1px solid #EAECF0" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 50,
                  backgroundColor: "#F2F4F7",
                  border: "1px solid #EAECF0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonOutlineIcon sx={{ color: "#6B7280" }} />
              </Box>
              <Box>
                <Typography fontSize={16} fontWeight={600}>
                  {users.length} {users.length === 1 ? "user" : "users"}
                </Typography>
                <Typography color="#475467">selected</Typography>
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 50,
                  backgroundColor: "#F2F4F7",
                  border: "1px solid #EAECF0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AssignmentOutlinedIcon sx={{ color: brand[600] }} />
              </Box>
              <Box>
                <Typography fontSize={16} fontWeight={600}>
                  {assignments.length}{" "}
                  {assignments.length === 1 ? "assignment" : "assignments"}
                </Typography>
                <Typography variant="body2" color="#475467">
                  selected
                </Typography>
              </Box>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              color: "#6D28D9",
              border: "1px solid  #D6BBFB",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "12px",
              "&:hover": {
                borderColor: "#5B21B6",
                backgroundColor: "rgba(109, 40, 217, 0.04)",
              },
            }}
          >
            View details
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              maxWidth: "561px",
              p: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography fontSize={18} fontWeight={600} mb="4px">
              Preview details
            </Typography>
            <Typography fontSize={14} fontWeight={400} color="#475467">
              Reviewing selected users and assignments independently.
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDialogOpen(false)}
            size="medium"
            sx={{ border: "none" }}
          >
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 0, borderTop: "1px solid #D0D5DD", pb: 0 }}>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: "12px 24px",
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1px solid #EAECF0",
                }}
              >
                <Typography fontSize={15} fontWeight={500}>
                  User
                </Typography>
                <Chip
                  label={users.length}
                  size="medium"
                  sx={{
                    height: 24,
                    width: 24,
                    backgroundColor: "#EFF8FF",
                    border: "1px solid #B2DDFF",
                    "& .MuiChip-label": { color: "#175CD3", padding: 0 },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {users.map((user) => (
                  <Box
                    key={user.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: "16px 24px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "9999px",
                        backgroundColor: "#F2F4F7",
                        border: "1px solid #EAECF0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PersonOutlineIcon
                        sx={{ color: "#667085", fontSize: 20 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontWeight: 500, fontSize: "14px" }}
                        noWrap
                      >
                        <EllipsisTooltip title={user.title}>
                          {user.title}
                        </EllipsisTooltip>
                      </Typography>
                      {user.subtitle && (
                        <Typography
                          color="text.secondary"
                          sx={{ fontSize: "14px" }}
                          noWrap
                        >
                          <EllipsisTooltip title={user.subtitle}>
                            {user.subtitle}
                          </EllipsisTooltip>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: "12px 24px",
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1px solid #EAECF0",
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  Assignments
                </Typography>
                <Chip
                  label={assignments.length}
                  size="medium"
                  sx={{
                    height: 24,
                    width: 24,
                    backgroundColor: "#EFF8FF",
                    border: "1px solid #B2DDFF",
                    "& .MuiChip-label": { color: "#175CD3", padding: 0 },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {assignments.map((assignment) => (
                  <Box
                    key={assignment.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: "16px 24px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "9999px",
                        backgroundColor: "#F2F4F7",
                        border: "1px solid #EAECF0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <AssignmentOutlinedIcon
                        sx={{ color: "#6B7280", fontSize: 20 }}
                      />
                    </Box>
                    <Typography
                      sx={{ fontWeight: 500, fontSize: "14px" }}
                      noWrap
                    >
                      <EllipsisTooltip title={assignment.title}>
                        {assignment.title}
                      </EllipsisTooltip>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
